import { useState, useEffect, useCallback } from 'react';
import { Loader, Sparkles, ChevronRight, Unplug } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatDateTime } from '../lib/text';
import DismissableInfoCard from './DismissableInfoCard';
import CandidateRow from './CandidateRow';
import VerdictNote, { byVerdict, type Verdict } from './VerdictNote';

// The Candidates tab: what a source matched to a document type, grouped by type.
// Two kinds of row — a whole folder whose name matched (linking it files its
// current and future contents), and an individual file whose name matched. After each
// sync Navaris judges each type's candidates together, files what is worth having and
// dismisses what fits no type; what is here it left unfiled, with its reason, recommends
// filing (from a source where people file), or has yet to judge. Linking a row downloads
// and reads it.
interface FileCand extends Verdict {
    id: string;              // file id
    source_filename: string;
    source_path: string | null;
    mime_type: string | null;
    document_type: string | null;
    document_type_id: string | null;
    file_date: string | null;
}

interface FolderCand extends Verdict {
    path: string;
    document_type: string | null;
    document_type_id: string | null;
    file_count: number;
}

interface Props {
    sourceId: string;
    onLinked?: () => void;
}

const UNGROUPED = 'Other documents';

function folderKey(f: FolderCand): string {
    return `folder:${f.document_type_id}:${f.path}`;
}
function leafName(path: string): string {
    const parts = path.split('/').filter(Boolean);
    return parts[parts.length - 1] || path;
}

export default function SourceReviewTab({ sourceId, onLinked }: Props) {
    const { authFetch } = useAuth();
    const { showToast } = useToast();
    const [files, setFiles] = useState<FileCand[]>([]);
    const [folders, setFolders] = useState<FolderCand[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<Set<string>>(new Set());
    const [openGroup, setOpenGroup] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await authFetch(`/sources/${sourceId}/candidates`);
            if (!res.ok) throw new Error('Failed to load candidates');
            const data = await res.json();
            setFiles(data.candidates || []);
            setFolders(data.folders || []);
        } catch (err) {
            console.error('Failed to load candidates:', err);
        } finally {
            setLoading(false);
        }
    }, [authFetch, sourceId]);

    useEffect(() => { load(); }, [load]);

    const groups = new Map<string, { folders: FolderCand[]; files: FileCand[] }>();
    const groupOf = (name: string | null) => {
        const key = name || UNGROUPED;
        let g = groups.get(key);
        if (!g) { g = { folders: [], files: [] }; groups.set(key, g); }
        return g;
    };
    for (const f of [...folders].sort(byVerdict)) groupOf(f.document_type).folders.push(f);
    for (const f of [...files].sort(byVerdict)) groupOf(f.document_type).files.push(f);
    const orderedGroups = [...groups.entries()].sort(([a], [b]) => {
        if (a === UNGROUPED) return 1;
        if (b === UNGROUPED) return -1;
        return a.localeCompare(b);
    });

    useEffect(() => {
        if (initialized || loading) return;
        if (orderedGroups.length > 0) {
            setOpenGroup(orderedGroups[0][0]);
            setInitialized(true);
        }
    }, [loading, initialized, orderedGroups]);

    const submit = useCallback(async (
        action: 'confirm' | 'dismiss', fileIds: string[], folderRefs: FolderCand[],
    ) => {
        if (fileIds.length === 0 && folderRefs.length === 0) return;
        const keys = [...fileIds, ...folderRefs.map(folderKey)];
        setBusy(prev => new Set([...prev, ...keys]));
        try {
            const res = await authFetch(`/sources/${sourceId}/candidates/${action}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    file_ids: fileIds,
                    folders: folderRefs.map(f => ({ path: f.path, document_type_id: f.document_type_id })),
                }),
            });
            if (!res.ok) throw new Error(`Failed to ${action}`);
            setFiles(prev => prev.filter(f => !fileIds.includes(f.id)));
            setFolders(prev => prev.filter(f => !keys.includes(folderKey(f))));
            if (action === 'confirm') {
                const n = fileIds.length + folderRefs.length;
                showToast(n > 1 ? `Linking ${n} items into the record` : 'Linking it into the record', 'success');
                onLinked?.();
            }
        } catch (err) {
            console.error(err);
            showToast(`Could not ${action === 'confirm' ? 'link' : 'dismiss'} that`, 'error');
        } finally {
            setBusy(prev => {
                const next = new Set(prev);
                keys.forEach(k => next.delete(k));
                return next;
            });
        }
    }, [authFetch, sourceId, showToast, onLinked]);

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Loader className="w-8 h-8 animate-spin text-brand" />
            </div>
        );
    }

    const total = files.length + folders.length;
    const recommendedFiles = files.filter(f => f.verdict === 'file');
    const recommendedFolders = folders.filter(f => f.verdict === 'file');
    const recommended = recommendedFiles.length + recommendedFolders.length;

    const toggle = (name: string) => setOpenGroup(prev => (prev === name ? null : name));

    return (
        <div>
            <DismissableInfoCard
                storageKey="sourceReview.introDismissed"
                title="Filing documents"
                icon={Sparkles}
            >
                After each sync, Navaris looks at every candidate for a kind of document together,
                files what is worth having, such as only your current financial model, and dismisses
                matches that are not your documents. These are the ones it left, with its reason, or
                recommends filing where you file this connection's documents yourself, grouped by what
                they appear to be. Linking a folder files
                everything inside it, now and in future, while linking a file files just that document.
                Dismiss anything not worth reading. You can also link files from the Files tab.
            </DismissableInfoCard>

            {total === 0 ? (
                <p className="text-center text-coolslate py-8">
                    Nothing waiting for you. Navaris looks at new files after each sync.
                </p>
            ) : (
                <>
                    <div className="flex justify-end gap-2 mb-3">
                        {recommended > 0 && (
                            <button
                                type="button"
                                onClick={() => submit('confirm', recommendedFiles.map(f => f.id), recommendedFolders)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium border rounded-md transition-colors cursor-pointer text-white bg-brand hover:bg-brand-dark border-brand"
                            >
                                <Sparkles className="w-3 h-3" />
                                Link recommended ({recommended})
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => submit('confirm', files.map(f => f.id), folders)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium border rounded-md transition-colors cursor-pointer text-insight bg-insight/8 hover:bg-insight/15 border-insight/20"
                        >
                            <Unplug className="w-3 h-3" />
                            Link all ({total})
                        </button>
                    </div>

                    <div className="border border-softgrey rounded-lg bg-white divide-y divide-softgrey overflow-hidden">
                        {orderedGroups.map(([group, g]) => {
                            const count = g.folders.length + g.files.length;
                            const isCollapsed = openGroup !== group;
                            return (
                                <div key={group}>
                                    <div className="flex items-center justify-between px-3 py-3.5">
                                        <button
                                            type="button"
                                            onClick={() => toggle(group)}
                                            className="flex items-center gap-2 min-w-0 flex-1 text-left cursor-pointer"
                                        >
                                            <ChevronRight
                                                className={`w-4 h-4 text-coolslate transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                                            />
                                            <span className="text-sm font-semibold text-graphite truncate">
                                                {group} <span className="text-coolslate font-normal">({count})</span>
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => submit('confirm', g.files.map(f => f.id), g.folders)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium border rounded-md transition-colors cursor-pointer shrink-0 text-insight bg-insight/8 hover:bg-insight/15 border-insight/20"
                                        >
                                            <Unplug className="w-3 h-3" />
                                            Link all
                                        </button>
                                    </div>

                                    <div
                                        className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${isCollapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'}`}
                                    >
                                        <div className="overflow-hidden min-h-0">
                                            <div className="px-3 pb-3 space-y-2">
                                                {g.folders.map((f) => (
                                                    <CandidateRow
                                                        key={folderKey(f)}
                                                        kind="folder"
                                                        title={leafName(f.path)}
                                                        titleHref={`/connections/${sourceId}/folder?path=${encodeURIComponent(f.path)}`}
                                                        subtitle={`${f.path} · ${f.file_count} file${f.file_count === 1 ? '' : 's'} · links future files too`}
                                                        primary={{ label: 'Link', busy: busy.has(folderKey(f)), recommended: f.verdict === 'file', onClick: () => submit('confirm', [], [f]) }}
                                                        onDismiss={() => submit('dismiss', [], [f])}
                                                    >
                                                        <VerdictNote verdict={f} typeId={f.document_type_id} />
                                                    </CandidateRow>
                                                ))}
                                                {g.files.map((s) => (
                                                    <CandidateRow
                                                        key={s.id}
                                                        kind="file"
                                                        title={s.source_filename}
                                                        titleHref={`/files/${s.id}`}
                                                        subtitle={(s.source_path || '').split('/').filter(Boolean).join(' / ') || undefined}
                                                        mimeType={s.mime_type}
                                                        meta={s.file_date ? formatDateTime(s.file_date, false) : undefined}
                                                        primary={{ label: 'Link', busy: busy.has(s.id), recommended: s.verdict === 'file', onClick: () => submit('confirm', [s.id], []) }}
                                                        onDismiss={() => submit('dismiss', [s.id], [])}
                                                    >
                                                        <VerdictNote verdict={s} typeId={s.document_type_id} />
                                                    </CandidateRow>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
